import React, { useMemo, useState } from "react";
import { Info, SlidersHorizontal, Pencil, RotateCcw, Loader2, Crown, ShieldCheck, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { RISK, downloadCsv, ExportBtn, Modal } from "./shared";
import { RoleIcon, ROLE_ACCENT, A, AccessCell, ACCESS_META } from "./ui";

const LEVEL_OPTIONS = [
  { value: "full", label: "Vollzugriff" },
  { value: "read", label: "Nur lesen" },
  { value: "limited", label: "Eingeschränkt" },
  { value: "none", label: "Kein Zugriff" },
];

export function RolesMatrixTab({ ctx }) {
  const { t, matrix, teamUsers, reload } = ctx;
  const [editKey, setEditKey] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const countByRole = useMemo(() => { const m = {}; (teamUsers || []).forEach((u) => { m[u.team_role] = (m[u.team_role] || 0) + 1; }); return m; }, [teamUsers]);

  if (!matrix) return <Card className="p-8 text-center" data-testid="sec-panel-roles"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></Card>;

  const accessOf = (r, sec) => (r.access && r.access[sec.key]) || (r.super_only ? "full" : "none");
  const editRole = matrix.roles.find((r) => r.key === editKey) || null;

  const exportMatrix = () => downloadCsv("berechtigungs-matrix.csv",
    [{ label: "Modul", value: (s) => s.label }, ...matrix.roles.map((r) => ({ label: r.label, value: (s) => t(ACCESS_META[accessOf(r, s)]?.label || "Kein Zugriff") }))],
    matrix.sections);

  return (
    <div className="space-y-6" data-testid="sec-panel-roles">
      <Card className="p-4 bg-[#A3E635]/[0.04] border-[#A3E635]/15">
        <div className="flex items-start gap-2.5">
          <Info className="w-4 h-4 text-[#A3E635] mt-0.5 shrink-0" />
          <p className="text-xs text-gray-400">{t("Verwalten Sie Rollen, Berechtigungen und Zugriffsrechte im gesamten System. Kritische Systembereiche (Benutzer & Sicherheit, Backup, QA, Protokoll, Einstellungen) bleiben ausschliesslich dem Super Admin vorbehalten.")}</p>
        </div>
      </Card>

      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {matrix.roles.map((r) => {
            const acc = ROLE_ACCENT[r.key] || "gray";
            const risk = RISK[r.risk] || RISK.minimal;
            const isSuper = r.is_super_admin || r.key === "super_admin";
            return (
              <Card key={r.key} className="p-5 relative overflow-hidden group" data-testid={`rolecard-${r.key}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1 ${A(acc).bar}`} />
                {isSuper
                  ? <span className="absolute right-4 top-4 inline-flex items-center gap-1 text-[10px] font-semibold text-[#A3E635]" data-testid={`rolecard-protected-${r.key}`}><Crown className="w-3.5 h-3.5" /> {t("Geschützt")}</span>
                  : <button onClick={() => setEditKey(r.key)} data-testid={`rolecard-edit-${r.key}`} title={t("Rolle bearbeiten")} className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>}
                <div className="flex items-center gap-2.5 mb-3 pr-8">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${A(acc).badge}`}><RoleIcon roleKey={r.key} className="w-[18px] h-[18px]" /></div>
                  <span className="font-bold text-sm">{r.label}</span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed mb-4 min-h-[60px]">{r.description}</p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5 gap-2">
                  <span className="text-xs text-gray-400"><span className="font-bold text-gray-200">{countByRole[r.key] || 0}</span> {t("Benutzer")}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${risk.cls}`}>{t(risk.label)}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${A(acc).badge}`}>{t(r.role_type || "Systemrolle")}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <Card className="p-0 overflow-hidden" data-testid="sec-roles-matrix">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-bold tracking-tight text-sm flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-[#A3E635]" /> {t("Berechtigungs-Matrix")}</div>
            <p className="text-[11px] text-gray-500 mt-0.5">{t("Übersicht aller Module und Funktionen nach Rolle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditKey("admin_ops")} data-testid="matrix-edit-role" className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 text-gray-300 transition-colors"><Pencil className="w-3.5 h-3.5" /> {t("Rolle bearbeiten")}</button>
            <button onClick={() => setRestoreOpen(true)} data-testid="matrix-defaults" className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 text-gray-300 transition-colors"><RotateCcw className="w-3.5 h-3.5" /> {t("Standardrollen")}</button>
            <ExportBtn onClick={exportMatrix} label={t("Export")} title={t("Als CSV exportieren")} testid="matrix-export" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-gray-500 border-b border-white/10">
                <th className="text-left font-semibold px-4 py-3 sticky left-0 bg-[#121217] z-10 min-w-[200px]">{t("Module & Funktionen")}</th>
                {matrix.roles.map((r) => (
                  <th key={r.key} className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                    <div className="flex flex-col items-center gap-1"><RoleIcon roleKey={r.key} className="w-4 h-4" /><span className="text-[10px] leading-tight max-w-[80px]">{r.label}</span></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.sections.map((sec) => (
                <tr key={sec.key} className={`border-b border-white/5 last:border-0 hover:bg-white/[0.015] ${sec.super_only ? "bg-red-500/[0.03]" : ""}`} data-testid={`matrix-row-${sec.key}`}>
                  <td className="px-4 py-2.5 font-medium whitespace-nowrap sticky left-0 bg-[#121217] z-10">
                    {sec.label}{sec.super_only && <span className="ml-2 text-[9px] font-semibold text-red-400/80 uppercase tracking-wide">{t("Kritischer Bereich")}</span>}
                  </td>
                  {matrix.roles.map((r) => (
                    <td key={r.key} className="px-3 py-2.5 text-center" data-testid={`matrix-cell-${sec.key}-${r.key}`}>
                      <AccessCell level={accessOf(r, sec)} t={t} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-white/10 flex items-center gap-5 flex-wrap text-[11px]" data-testid="matrix-legend">
          {Object.entries(ACCESS_META).map(([k, m]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-gray-400">
              {m.icon ? <m.icon className={`w-3.5 h-3.5 ${m.cls}`} /> : <span className={`text-base leading-none ${m.cls}`}>•</span>} {t(m.label)}
            </span>
          ))}
        </div>
      </Card>

      {editRole && <RolePermEditor key={editRole.key} ctx={ctx} role={editRole} roles={matrix.roles.filter((r) => !r.is_super_admin && r.key !== "super_admin")} sections={matrix.sections} onClose={() => setEditKey(null)} onPick={setEditKey} onDone={() => { setEditKey(null); reload && reload(); }} />}
      {restoreOpen && <RestoreDefaultsModal t={t} onClose={() => setRestoreOpen(false)} onDone={() => { setRestoreOpen(false); reload && reload(); }} />}
    </div>
  );
}

function RolePermEditor({ ctx, role, roles, sections, onClose, onPick, onDone }) {
  const { t } = ctx;
  const [access, setAccess] = useState(() => ({ ...(role.access || {}) }));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const acc = ROLE_ACCENT[role.key] || "gray";
  const editable = sections.filter((s) => !s.super_only);
  const critical = sections.filter((s) => s.super_only);
  const changes = editable.filter((s) => (role.access || {})[s.key] !== access[s.key]).length;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      editable.forEach((s) => { payload[s.key] = access[s.key] || "none"; });
      const { data } = await api.put(`/admin/roles/${role.key}/permissions`, { access: payload });
      toast.success(t("Berechtigungen gespeichert") + (data?.changed ? ` · ${data.changed} ${t("Änderungen")}` : ""));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); setConfirm(false); }
  };

  return (
    <Modal title={t("Rolle bearbeiten")} onClose={onClose} testid="role-perm-editor" wide>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs text-gray-500">{t("Rolle")}:</span>
        <select value={role.key} onChange={(e) => onPick(e.target.value)} data-testid="role-perm-select" className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:border-[#A3E635] outline-none">
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${A(acc).badge}`}><RoleIcon roleKey={role.key} className="w-3.5 h-3.5" /> {role.label}</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">{role.description}</p>

      <div className="rounded-xl border border-white/10 bg-black/20 divide-y divide-white/[0.06] max-h-[46vh] overflow-y-auto" data-testid="role-perm-sections">
        {editable.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`perm-row-${s.key}`}>
            <span className="text-sm text-gray-200">{s.label}</span>
            <select value={access[s.key] || "none"} onChange={(e) => setAccess((a) => ({ ...a, [s.key]: e.target.value }))} data-testid={`perm-select-${s.key}`}
              className="bg-black/50 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs focus:border-[#A3E635] outline-none min-w-[140px]">
              {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
            </select>
          </div>
        ))}
        {critical.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-3 px-3 py-2 opacity-70" data-testid={`perm-row-${s.key}`}>
            <span className="text-sm text-gray-300 flex items-center gap-2">{s.label} <span className="text-[9px] font-semibold text-red-400/80 uppercase tracking-wide">{t("Kritischer Bereich")}</span></span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-300 min-w-[140px] justify-center"><Lock className="w-3.5 h-3.5" /> {t("Nur Super Admin")}</span>
          </div>
        ))}
      </div>

      {!confirm ? (
        <div className="flex items-center justify-between gap-2 mt-5">
          <span className="text-[11px] text-gray-500">{changes > 0 ? `${changes} ${t("Änderungen")}` : t("Keine Änderungen")}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
            <button onClick={() => setConfirm(true)} disabled={changes === 0} data-testid="role-perm-save" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"><ShieldCheck className="w-4 h-4" /> {t("Speichern")}</button>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid="role-perm-confirm">
          <p className="text-sm text-gray-300 mb-3">{t("Berechtigungen der Rolle")} <span className="font-semibold">{role.label}</span> {t("ändern? Die Änderung wird protokolliert.")}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirm(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Zurück")}</button>
            <button onClick={save} disabled={busy} data-testid="role-perm-confirm-yes" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Bestätigen & speichern")}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RestoreDefaultsModal({ t, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin/roles/restore-defaults"); toast.success(t("Standardrollen wiederhergestellt")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Standardrollen wiederherstellen")} onClose={onClose} testid="restore-defaults-modal">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0"><RotateCcw className="w-5 h-5 text-amber-400" /></div>
        <p className="text-sm text-gray-300">{t("Alle Rollen-Berechtigungen werden auf die Standardwerte zurückgesetzt. Eigene Anpassungen gehen verloren. Die Aktion wird protokolliert.")}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy} data-testid="restore-defaults-yes" className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} {t("Zurücksetzen")}</button>
      </div>
    </Modal>
  );
}
